import { VideoPlayerType } from './../models/camera.model';
import * as aspSettings from './aspSettings';
import * as units from '../common/units';
import * as utils from '../common/utils';
import { AspObject, ObjectType } from './AspObject';
import { IObjectsPool } from './interfaces';
import { Location } from '../common/Location';
import * as consts from '../common/consts';
import { BehaviorSubject } from 'rxjs';

const SENSOR_COLOR_OFF = [0.9, 0.18, 0.18, 1]; /* RED */
const SENSOR_COLOR_CSS_OFF = utils.newmiColorToString(SENSOR_COLOR_OFF);

export class SensorCoreObject extends AspObject {
  // graphics properties
  private Color: number[] = [0.75, 0.75, 0.75, 1];
  private ColorCss: string = "";
  private Symbol: string = "";
  private SymbolCss: string = "";

  public ObjectUpdate = new BehaviorSubject<boolean>(false);

  json: any = null;

  public isRadar: boolean = false;
  datetime: string;
  sic: number;
  sac: number;
  name: string;
  SensorType: string;
  providerCameraName: VideoPlayerType;
  workModeType: number;
  is2D: boolean;

  // Credibility
  shortCred: number;
  mediumCred: number;
  longCred: number;

  // NAI NAT
  naiNatArray: any;
  isNatNaiVisible: boolean;

  isZory: boolean = false;
  public isAcoustic: boolean = false;
  public isVideo: boolean = false;
  isNewCamera: boolean = false;
  isEditCamera: boolean = false;
  // VideoStuff
  videoLink: string;
  videoLinkToken: string;
  streamName: string;
  status: boolean;
  res: string = 'N/A';

  // extra details...
  logiConn: boolean;
  scanTime: number;
  minRange: number;
  maxRange: number;
  maxHeight: number;
  SSRCapable: boolean;
  SSRMaxRange: number;
  SSRMinRange: number;

  PSRReg: boolean;
  SSRReg: boolean;
  ALTReg: boolean;
  azimuthBias: number;
  rangeBias: number;
  heightBias: number;
  elevationBias: number;
  SSRAzimuthBias: number;
  SSRRangeBias: number;
  locXBias: number;
  locYBias: number;
  locConfidence: boolean;

  azimuthError: number;
  rangeError: number;
  heightError: number;
  elevationError: number;
  SSRAzimuthError: number;
  SSRRangeError: number;
  locBiasXError: number;
  locBiasYError: number;
  rangeRateError: number;

  SRPlots: number;
  SRRejPlots: number;
  SSRPlots: number
  SSRRejPlots: number;
  strobes: number;
  rejStrobes: number;

  constructor(objectsPool: IObjectsPool, public sensorID: number, public sensorLetter: string) {
    super(objectsPool, ObjectType.Sensor, AspObject.generateId(ObjectType.Sensor, sensorID));
  }

  handleMsg(msgObj: any): boolean {
    if (msgObj.MessageType == consts.MSG_TYPE_SENSOR) {

      if (msgObj.IsAcoustic) {
        this.isAcoustic = msgObj.IsAcoustic;
        this.Color = [1.00, 0.00, 0.00, 1]; /* red */
        this.Symbol = "sound-sns";
      }
      else {
        this.Color = [1.00, 0.65, 0.00, 1]; /* orange */
        this.Symbol = "radar";
        this.isRadar = true;
      }
      this.ColorCss = utils.newmiColorToString(this.Color);
      this.SymbolCss = utils.getPlaneSymbol(this.Symbol);

      this.sic = msgObj.Sic;
      this.sac = msgObj.Sac;
      this.SensorType = msgObj.SensorType;
      if (this.SensorType == "2D")
        this.is2D = true;
      else
        this.is2D = false;

      if (msgObj.streamName)
        this.streamName = msgObj.streamName;
      else
        this.streamName = "N/A";
      if (msgObj.Name)
        this.name = msgObj.Name;
      else
        this.name = "N/A";

      if (msgObj.Credibility) {
        let cred = msgObj.Credibility;
        this.shortCred = cred.Short;
        this.mediumCred = cred.Medium;
        this.longCred = cred.Long;
      }

      if (msgObj.IsZori) {
        this.isZory = true;
      }

      this.sensorLetter = msgObj.Letter;

      let loc: Location = { altitude: msgObj.LocationAltitude, longitude: msgObj.LocationLong, latitude: msgObj.LocationLat };
      if (typeof loc != 'undefined') {
        this.updateDynamics((loc.longitude/* * units.RAD_TO_DEG*/) + aspSettings.deltaX,(loc.latitude/* * units.RAD_TO_DEG*/) + aspSettings.deltaY, loc.altitude);
      }
      this.datetime = units.datetime2Str(new Date());

      // all extra details...
      // this.logiConn = msgObj.ExtraDetails.LogicallyConnected;
      // this.scanTime = msgObj.ExtraDetails.ScanTime / 1000;
      // this.minRange = msgObj.ExtraDetails.MinRange;
      // this.maxRange = msgObj.ExtraDetails.MaxRange;
      // this.maxHeight = msgObj.ExtraDetails.MaxHeight;
      // this.SSRCapable = msgObj.ExtraDetails.SSRCapable;
      // this.SSRMaxRange = msgObj.ExtraDetails.SSRMaxRange;
      // this.SSRMinRange = msgObj.ExtraDetails.SSRMinRange;

      // this.PSRReg = msgObj.ExtraDetails.PSRRegistration;
      // this.SSRReg = msgObj.ExtraDetails.SSRRegistration;
      // this.ALTReg = msgObj.ExtraDetails.ALTRegistration;
      // this.azimuthBias = msgObj.ExtraDetails.AzimuthBias * units.RAD_TO_DEG;
      // this.rangeBias = msgObj.ExtraDetails.RangeBias;
      // this.heightBias = msgObj.ExtraDetails.HeightBias;
      // this.elevationBias = msgObj.ExtraDetails.ElevationBias * units.RAD_TO_DEG;
      // this.SSRAzimuthBias = msgObj.ExtraDetails.SSRAzimuthBias * units.RAD_TO_DEG;
      // this.SSRRangeBias = msgObj.ExtraDetails.SSRRangeBias;
      // this.locXBias = msgObj.ExtraDetails.LocXBias;
      // this.locYBias = msgObj.ExtraDetails.LocYBias;
      // this.locConfidence = msgObj.ExtraDetails.LocConfidence;

      // this.azimuthError = msgObj.ExtraDetails.AzimuthError * units.RAD_TO_DEG;
      // this.rangeError = msgObj.ExtraDetails.RangeError;
      // this.heightError = msgObj.ExtraDetails.HeightError;
      // this.elevationError = msgObj.ExtraDetails.ElevationError * units.RAD_TO_DEG;
      // this.SSRAzimuthError = msgObj.ExtraDetails.SSRAzimuthError * units.RAD_TO_DEG;
      // this.SSRRangeError = msgObj.ExtraDetails.SSRRangeError;
      // this.locBiasXError = msgObj.ExtraDetails.LocBiasXError;
      // this.locBiasYError = msgObj.ExtraDetails.LocBiasYError;
      // this.rangeRateError = msgObj.ExtraDetails.RangeRateError;

      // this.SRPlots = msgObj.ExtraDetails.SRPlots;
      // this.SRRejPlots = msgObj.ExtraDetails.SRRejPlots;
      // this.SSRPlots = msgObj.ExtraDetails.SSRPlots;
      // this.SSRRejPlots = msgObj.ExtraDetails.SSRRejPlots;
      // this.strobes = msgObj.ExtraDetails.Strobes;
      // this.rejStrobes = msgObj.ExtraDetails.RejStrobes;

      this.postHandleMsg(msgObj);

      this.notifyDataChange();
      this.ObjectUpdate.next(true);

      return true;
    }
    return false;
  }

  handleVid(msgObj: any): boolean {
    this.isVideo = true;
    this.Color = [0.10, 0.60, 0.20, 1.0]; /* green */
    this.Symbol = "video";
    this.ColorCss = utils.newmiColorToString(this.Color);
    this.SymbolCss = utils.getPlaneSymbol(this.Symbol);

    this.SensorType = "Video";
    this.workModeType = msgObj.type;
    if (msgObj.providerCameraId){
      this.providerCameraName = msgObj.providerCameraId;
    }
    this.is2D = true; // IDK

    this.name = msgObj.name;
    this.sensorLetter = '?';

    let loc: Location = { altitude: 1500, longitude: msgObj.locationX ? msgObj.locationX : 0, latitude: msgObj.locationY ? msgObj.locationY : 0 };
    if (typeof loc != 'undefined') {
      this.updateDynamics(loc.longitude + aspSettings.deltaX,
        loc.latitude + aspSettings.deltaY, loc.altitude);
    }

    this.datetime = units.datetime2Str(new Date());
    if (msgObj.link)
      this.videoLink = msgObj.link;
    else
      this.videoLink = "";

    if (msgObj.streamName)
      this.streamName = msgObj.streamName;
    else
      this.streamName = "N/A";

    // !! TBD STATUS, TYPE LINKTYPE???
    if (msgObj.status === 2)
      this.status = false;
    else
      this.status = true;

    if (msgObj.creationUser) // ! yes for some reason creationUser is the res.... not my fault... triple-c fault...
      this.res = msgObj.creationUser;

    this.postHandleMsg(msgObj);

    this.notifyDataChange();
    this.ObjectUpdate.next(true);

    return true;
  }

  protected postHandleMsg(msgObj: any) {
    // To be implemented if needed
  }

  // Get object title
  getTitle(): string {
    if (this.isRadar)
      return ('Sensor: ') + this.sic + ' (' + this.sensorLetter + ')';
    if (this.isVideo)
      return ('Video: ') + this.name;
    if (this.isAcoustic)
      return ('Acoustic: ') + this.sensorID;
  }

  getFullTitle

  // Override AspObject
  getColor() {
    if (this.status)
      return this.Color
    return SENSOR_COLOR_OFF;
  }

  // color for css html 
  getCssColor() {
    if (this.status)
      return this.ColorCss;
    return SENSOR_COLOR_CSS_OFF;
  }

  // gets the svg icon name
  getSymbol(ForceSnsSym: boolean = false): string {
    return this.Symbol
  }

  // gets full path and the icon
  getSvgSymbol() {
    return this.SymbolCss;
  }

  // Get object searchable identifiers (to be used by Search function)
  getIdentifiers(): string[] {
    let identifiers = [];
    identifiers.push(this.id.toString().toLowerCase());
    identifiers.push("חיישן");
    identifiers.push("sensor");
    if (this.isRadar) {
      identifiers.push(this.sic.toString().toLowerCase());
      identifiers.push(this.sac.toString().toLowerCase());
      identifiers.push("מכם");
      identifiers.push("radar");
    }
    if (this.isVideo) {
      identifiers.push("וידאו");
      identifiers.push("video");
      if (this.name)
        identifiers.push(this.name.toLowerCase());
      if (this.streamName)
        identifiers.push(this.streamName);
    }
    if (this.isAcoustic) {
      identifiers.push("סאונד");
      identifiers.push("sound");
    }
    return identifiers;
  }
}
